import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import { userService } from "../../services/userService";
import { analyticsService } from "../../services/analyticsService";
import { useAuthActions } from "@/hooks/auth/useAuth";
import { firebaseService } from "../../lib/firebase";

interface GoogleSignInButtonProps {
  onExistingUser: (isExistingUser: boolean) => void;
  onNewUser: (data: {
    firstName: string;
    lastName: string;
    email: string;
  }) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  config: any; // Replace with proper type
  propertyId?: string;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onExistingUser,
  onNewUser,
  isLoading,
  setIsLoading,
  setError,
  config,
  propertyId,
}) => {
  const { setIsInGoogleSignInFlow } = useAuthActions();
  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Set flag before starting Google sign-in
      setIsInGoogleSignInFlow(true);

      if (!config?.id) {
        throw new Error("Cannot sign in without organization context");
      }

      const result = await firebaseService.auth.signInWithGoogle();
      const user = result.user;

      // Check if this Google account is already an IDX web user (Spanner) via
      // idx-validate — the token from the just-completed sign-in is used.
      const { valid } = await userService.validateWebUser(user.uid, config.id);
      if (valid) {
        // User exists, clear flag and keep them signed in

        setIsInGoogleSignInFlow(false);
        analyticsService.trackEvent({
          orgId: config?.id || "default",
          tenantId: config.id,
          eventType: "conversion",
          category: "Registration",
          action: "Google Sign In Success",
          isAuthenticated: true,
          userType: "lead",
          data: {
            userId: user.uid,
            path: window.location.pathname,
            ...(propertyId ? { propertyId } : {}),
            tenantName: config.name,
            source: document.referrer,
            utmSource:
              new URLSearchParams(window.location.search).get("utm_source") ||
              "direct",
          },
        });
        onExistingUser(true);
      } else {
        // New user - get info and pre-fill form
        const nameParts = user.displayName?.split(" ") || ["", ""];

        // Delete the auth user since we just want their info
        await user.delete();

        // The Google sign-in flow is finished on this terminal path too, so
        // clear the flag — leaving it set keeps useAuth suppressing
        // contact-profile resolution, stranding a later sign-in in this tab.
        setIsInGoogleSignInFlow(false);

        onNewUser({
          firstName: nameParts[0],
          lastName: nameParts[1],
          email: user.email || "",
        });
      }
    } catch (err) {
      // Clear flag on error

      setIsInGoogleSignInFlow(false);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during Google sign-in"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleGoogleSignIn}
      type="button"
      disabled={isLoading}
      className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      Continue with Google
    </button>
  );
};

export default GoogleSignInButton;
